// GitHub API client — fetch nativo, sin Octokit. PAT vive en localStorage.
// 4 endpoints REST + tipos co-localizados con solo los campos que renderizamos.

const TOKEN_KEY = 'wiphub_pat'
const API_BASE = 'https://api.github.com'

// Fallback en memoria: algunos browsers de Smart TV no persisten localStorage
// (ni siquiera entre F5 dentro de la misma sesión). Mantenemos el token en
// memoria del módulo así el dashboard funciona aunque localStorage esté roto;
// si la TV reinicia el browser, el token se recupera del hash #pat=... de la URL
// (ver loadTokenFromUrlHash en main.tsx + README).
let inMemoryToken: string | null = null

export class TokenInvalid extends Error {
  constructor() {
    super('GitHub token inválido o expirado.')
    this.name = 'TokenInvalid'
  }
}

export class RateLimited extends Error {
  constructor(public resetAt: Date) {
    super(`GitHub rate limit alcanzado. Reset en ${resetAt.toLocaleTimeString('es-CL')}.`)
    this.name = 'RateLimited'
  }
}

// Store de rate limit — useSyncExternalStore lo consume desde RateLimitBanner.
// Cuando ghFetch detecta 403, actualiza el store. Listeners reaccionan.
let rateLimitedUntil: Date | null = null
const rateLimitListeners = new Set<() => void>()

export function subscribeRateLimit(cb: () => void): () => void {
  rateLimitListeners.add(cb)
  return () => rateLimitListeners.delete(cb)
}

export function getRateLimitedUntil(): Date | null {
  return rateLimitedUntil
}

function setRateLimited(date: Date) {
  if (rateLimitedUntil && rateLimitedUntil >= date) return
  rateLimitedUntil = date
  rateLimitListeners.forEach((cb) => cb())
}

export function getToken(): string | null {
  if (inMemoryToken) return inMemoryToken
  try {
    const stored = localStorage.getItem(TOKEN_KEY)
    if (stored) inMemoryToken = stored
    return stored
  } catch {
    return null
  }
}

export function setToken(token: string): void {
  inMemoryToken = token
  try {
    localStorage.setItem(TOKEN_KEY, token)
  } catch {
    // browsers de Smart TV pueden bloquear localStorage; quedamos en memoria.
  }
}

export function clearToken(): void {
  inMemoryToken = null
  try {
    localStorage.removeItem(TOKEN_KEY)
  } catch {
    // ignore
  }
}

export function hasToken(): boolean {
  return !!getToken()
}

// Lee #pat=... del hash de la URL al cargar. Si encuentra un PAT, lo persiste
// (best-effort en localStorage + siempre en memoria) y limpia el hash. Útil para
// browsers de Smart TV que no persisten localStorage: el bookmark de la TV
// incluye el PAT en el hash y se re-aplica en cada apertura.
export function loadTokenFromUrlHash(): void {
  if (typeof window === 'undefined' || !window.location.hash) return
  const hash = window.location.hash.replace(/^#/, '')
  const params = new URLSearchParams(hash)
  const fromHash = params.get('pat')
  if (!fromHash) return

  setToken(fromHash)

  // Limpiar el hash de la URL bar sin recargar.
  params.delete('pat')
  const remaining = params.toString()
  const newHash = remaining ? `#${remaining}` : ''
  window.history.replaceState(null, '', window.location.pathname + window.location.search + newHash)
}

async function ghFetch<T>(path: string): Promise<T> {
  const token = getToken()
  if (!token) throw new TokenInvalid()

  const res = await fetch(`${API_BASE}${path}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/vnd.github+json',
      'X-GitHub-Api-Version': '2022-11-28',
    },
  })

  if (res.status === 401) throw new TokenInvalid()
  if (res.status === 403 && res.headers.get('x-ratelimit-remaining') === '0') {
    const resetSec = Number(res.headers.get('x-ratelimit-reset') ?? '0')
    const resetAt = new Date(resetSec * 1000)
    setRateLimited(resetAt)
    throw new RateLimited(resetAt)
  }
  if (!res.ok) throw new Error(`GitHub API ${res.status}: ${res.statusText}`)

  return res.json() as Promise<T>
}

// ============================================================================
// Tipos — solo los campos que renderizamos. NO usar tipos completos GitHub.
// ============================================================================

export interface GhUser {
  login: string
  avatar_url: string
}

export interface GhLabel {
  name: string
  color: string
}

export interface Issue {
  number: number
  title: string
  html_url: string
  state: 'open' | 'closed'
  created_at: string
  user: GhUser | null
  labels: GhLabel[]
  pull_request?: unknown
}

export interface PR {
  number: number
  title: string
  html_url: string
  draft: boolean
  created_at: string
  updated_at: string
  body: string | null
  user: GhUser | null
  head: { sha: string; ref: string }
  requested_reviewers: GhUser[]
}

export interface Run {
  id: number
  status: 'queued' | 'in_progress' | 'completed'
  conclusion: 'success' | 'failure' | 'cancelled' | 'skipped' | 'timed_out' | 'neutral' | null
  head_branch: string
  head_sha: string
  created_at: string
  html_url: string
}

export interface Commit {
  sha: string
  html_url: string
  commit: {
    message: string
    author: { name: string; date: string }
  }
  author: GhUser | null
}

// ============================================================================
// Endpoints
// ============================================================================

export async function fetchUser(): Promise<GhUser> {
  return ghFetch<GhUser>('/user')
}

// Repo tal como lo devuelve /user/repos — solo los campos que necesitamos para
// descubrir la lista dinámica del board (identidad visual la resuelve repos.ts).
export interface RepoInfo {
  full_name: string
  name: string
  archived: boolean
  fork: boolean
  pushed_at: string
}

// Lista dinámica de repos del board: todos los repos NO archivados de la cuenta
// wipxap, ordenados por push más reciente (los activos rotan primero). El PAT es
// del owner (Luis) → affiliation=owner trae también los privados. Reemplaza la
// antigua const REPOS fija. per_page=100 basta (la cuenta no supera ~30 repos).
export async function fetchRepos(): Promise<RepoInfo[]> {
  const data = await ghFetch<RepoInfo[]>(
    '/user/repos?affiliation=owner&per_page=100&sort=pushed&direction=desc',
  )
  return data.filter((r) => !r.archived && r.full_name.startsWith('wipxap/'))
}

async function ghMutate(path: string, method: 'POST' | 'PATCH' | 'DELETE', body?: unknown): Promise<unknown> {
  const token = getToken()
  if (!token) throw new TokenInvalid()
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/vnd.github+json',
      'X-GitHub-Api-Version': '2022-11-28',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  })
  if (res.status === 401) throw new TokenInvalid()
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`GitHub ${method} ${path} → ${res.status}: ${text.slice(0, 120)}`)
  }
  return res.status === 204 ? null : res.json()
}

export async function addLabelsToIssue(repo: string, issueNumber: number, labels: string[]): Promise<void> {
  await ghMutate(`/repos/${repo}/issues/${issueNumber}/labels`, 'POST', { labels })
}

// DELETE /repos/{repo}/issues/{n}/labels/{name} — quita UN label específico.
// GitHub espera el label name URL-encoded en el path (no en body).
export async function removeLabelFromIssue(repo: string, issueNumber: number, label: string): Promise<void> {
  await ghMutate(`/repos/${repo}/issues/${issueNumber}/labels/${encodeURIComponent(label)}`, 'DELETE')
}

export async function fetchIssues(repo: string): Promise<Issue[]> {
  const data = await ghFetch<Issue[]>(`/repos/${repo}/issues?state=open&per_page=100`)
  // /issues endpoint también devuelve PRs — filtrarlas explícitamente.
  return data.filter((i) => !i.pull_request)
}

// Issues cerrados dentro de la ventana `since` (ISO). Necesario para asociar
// merges a su issue padre incluso después de que el PR cerró el issue. Sin
// esto, los threads completados desaparecerían del dashboard antes de ser
// reportados al usuario (decisión de producto).
export async function fetchRecentlyClosedIssues(repo: string, sinceIso: string): Promise<Issue[]> {
  const data = await ghFetch<Issue[]>(
    `/repos/${repo}/issues?state=closed&since=${encodeURIComponent(sinceIso)}&per_page=100`,
  )
  return data.filter((i) => !i.pull_request)
}

export async function fetchPRs(repo: string): Promise<PR[]> {
  return ghFetch<PR[]>(`/repos/${repo}/pulls?state=open&per_page=50`)
}

export interface MergedPR {
  number: number
  title: string
  html_url: string
  merged_at: string | null
  body: string | null
  user: GhUser | null
  base: { ref: string }
  head: { sha: string; ref: string }
  labels: GhLabel[]
}

export interface ClosedUnmergedPR extends PR {
  closed_at: string | null
  merged_at: null
}

// Tipo crudo de /pulls?state=closed: GitHub devuelve mergeados + cerrados-sin-
// merge MEZCLADOS en la misma respuesta. Una SOLA llamada por repo; los dos sets
// (merged / closed-unmerged) se derivan client-side (useMergedPRs /
// useClosedUnmergedPRs comparten esta query → sin request duplicado).
// sort=updated&direction=desc trae los más recientes; per_page=100 cubre la
// ventana retroactiva sin paginar (un repo activo hace ~50 merges/mes).
export type ClosedPull = PR & {
  merged_at: string | null
  closed_at: string | null
  base: { ref: string }
  labels: GhLabel[]
}

export async function fetchClosedPulls(repo: string): Promise<ClosedPull[]> {
  return ghFetch<ClosedPull[]>(
    `/repos/${repo}/pulls?state=closed&sort=updated&direction=desc&per_page=100`,
  )
}

export async function fetchWorkflowRuns(repo: string): Promise<Run[]> {
  const data = await ghFetch<{ workflow_runs: Run[] }>(
    `/repos/${repo}/actions/runs?branch=main&per_page=5`,
  )
  return data.workflow_runs
}

export async function fetchCommits(repo: string): Promise<Commit[]> {
  // Ventana de 40 días: cubre cualquier mes calendario completo (hasta 31 días)
  // con margen — alimenta las estadísticas de equipo (día/semana/mes). El chip
  // "N·24h" del header del proyecto filtra client-side las últimas 24h sobre
  // este mismo dataset (ProjectSection), sin un request extra.
  const since = new Date(Date.now() - 40 * 86_400_000).toISOString()
  return ghFetch<Commit[]>(`/repos/${repo}/commits?sha=main&since=${since}&per_page=100`)
}
